import type { TourStep } from '../types/onboarding';
import type { MissionStep } from '../../shared/onboarding/missionTypes';
import { shouldSkipModuleDashboardStep } from '../../shared/onboarding/dashboardModuleTour';
import { TOUR_DOM_EVENTS } from '../components/onboarding/onboardingDomEvents';
import {
  queryTourTarget,
  waitForTarget,
} from '../components/onboarding/engine/waitForTarget';
import type { CoordinatorLabTourSlug } from '../../shared/onboarding/coordinatorLearningLabTours';
import { setCreateMissionDraftReady } from '../../shared/onboarding/missionSessionStorage';
import { scrollElementIntoSafeViewport } from '../../shared/onboarding/missionViewport';
import { ensureMissionRouteForTourStep } from '../utils/missionRouteEnsure';

function embedSubTabFromAnchor(anchor: string | undefined): string | null {
  if (!anchor?.startsWith('embed-tab-')) {
    return null;
  }
  return anchor.slice('embed-tab-'.length);
}

async function switchCertificateSubTab(tab: string): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  if (tab === 'overview') {
    params.delete('certTab');
  } else {
    params.set('certTab', tab);
  }
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${params.toString()}`,
  );
  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.SET_CERTIFICATE_TAB, { detail: { tab } }),
  );
  await new Promise((resolve) => window.setTimeout(resolve, 250));
}

async function switchEmbedSubTab(tab: string): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  params.set('embedTab', tab);
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${params.toString()}`,
  );
  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.SET_EMBED_TAB, { detail: { tab } }),
  );
  await new Promise((resolve) => window.setTimeout(resolve, 250));
}

async function scrollTourTargetIntoView(step: TourStep): Promise<void> {
  if (!step.target || step.target === 'body') {
    return;
  }
  const el = queryTourTarget(step.target);
  if (el instanceof HTMLElement) {
    scrollElementIntoSafeViewport(el);
  }
}

const EVENT_FORM_CREATE_PATH = '/coordinator-event-create?learningLab=1';
const TOUR_NAVIGATE_EVENT = 'apt-tour:navigate';

function tourAnchorFromStep(step: TourStep): string {
  const metadata = step.metadata ?? {};
  if (typeof metadata.tourAnchor === 'string' && metadata.tourAnchor.length > 0) {
    return metadata.tourAnchor;
  }
  if (typeof step.target !== 'string') {
    return '';
  }
  return step.target.match(/data-tour(?:-stable)?=["']([^"']+)["']/)?.[1] ?? '';
}

function isOnEventFormRoute(): boolean {
  return window.location.pathname.includes('/coordinator-event');
}

async function ensureEventFormRoute(step: TourStep): Promise<void> {
  const anchor = tourAnchorFromStep(step);
  if (!anchor.startsWith('event-form-')) {
    return;
  }
  if (isOnEventFormRoute()) {
    return;
  }
  setCreateMissionDraftReady(true);
  window.dispatchEvent(
    new CustomEvent(TOUR_NAVIGATE_EVENT, {
      detail: { path: EVENT_FORM_CREATE_PATH },
    }),
  );
  await new Promise((resolve) => window.setTimeout(resolve, 600));
  if (step.target && step.target !== 'body') {
    await waitForTarget(step.target, { timeoutMs: 10000 });
  }
}

async function jumpStepperToIndex(stepIndex: number): Promise<void> {
  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.STEPPER_STEP_CHANGED, {
      detail: { stepIndex, requested: true },
    }),
  );
  await new Promise((resolve) => window.setTimeout(resolve, 200));
}

export function enrichMissionTourStep(
  step: TourStep,
  missionSlug?: CoordinatorLabTourSlug,
): TourStep {
  const metadata = step.metadata ?? {};
  const tourAnchor = String(metadata.tourAnchor ?? '');
  const embedTab =
    (metadata.embedTab as string | undefined) ??
    embedSubTabFromAnchor(
      typeof step.target === 'string'
        ? step.target.match(/data-tour(?:-stable)?=["']([^"']+)["']/)?.[1]
        : undefined,
    );
  const coordinatorTabSlug = metadata.coordinatorTabSlug as string | undefined;
  const module = metadata.module as string | undefined;
  const stepperIndex = metadata.stepperIndex as number | undefined;
  const certificateTab = metadata.certificateTab as string | undefined;
  const isPanelTarget = metadata.isPanelTarget === true;

  const specLike = tourAnchor
    ? {
        stepName: step.stepName ?? '',
        tourAnchor,
        title: String(step.title ?? ''),
        content: String(step.content ?? ''),
        module: module as 'coordinator' | undefined,
        coordinatorTabSlug,
        isPanelTarget,
      }
    : undefined;

  const needsTabSwitch = Boolean(coordinatorTabSlug && module);

  const beforeAction = async () => {
    if (missionSlug) {
      await ensureMissionRouteForTourStep(step, missionSlug);
    }

    await ensureEventFormRoute(step);

    if (typeof stepperIndex === 'number') {
      await jumpStepperToIndex(stepperIndex);
    }

    if (needsTabSwitch) {
      window.dispatchEvent(
        new CustomEvent(TOUR_DOM_EVENTS.SWITCH_DASHBOARD_MODULE_TAB, {
          detail: {
            module,
            coordinatorTabSlug,
          },
        }),
      );
      await new Promise((resolve) => window.setTimeout(resolve, 350));
    }

    if (embedTab) {
      await switchEmbedSubTab(embedTab);
    }

    if (
      certificateTab === 'overview' ||
      certificateTab === 'templates' ||
      certificateTab === 'issued'
    ) {
      await switchCertificateSubTab(certificateTab);
    }

    if (step.target && step.target !== 'body') {
      await scrollTourTargetIntoView(step);
      await waitForTarget(step.target, { timeoutMs: 8000 });
    }

    if (metadata.enableDraftRedirect) {
      setCreateMissionDraftReady(true);
    }
  };

  return {
    ...step,
    tip: step.tip ?? (metadata.tip as string | undefined),
    skipIf: specLike
      ? () => shouldSkipModuleDashboardStep(specLike)
      : step.skipIf,
    beforeAction,
  };
}

export function missionStepFromTourStep(step: TourStep): Partial<MissionStep> {
  const metadata = step.metadata ?? {};
  return {
    id: String(metadata.missionStepId ?? step.stepName ?? ''),
    advanceGate: metadata.advanceGate as MissionStep['advanceGate'],
    optional: metadata.optional === true,
  };
}
